/**
 * ⚡ Optimized Telemetry Chart Component v2.5.0
 *
 * Modern redesign:
 * - Reactive to screen rotation (useWindowDimensions)
 * - Metric selector tabs
 * - Modern card UI with gradient header accent
 * - Smooth animated stat chips
 * - Fixed landscape fullscreen layout
 */

import { Ionicons } from '@expo/vector-icons';
import React, { useMemo, useState } from 'react';
import {
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { LineChart } from 'react-native-chart-kit';
import logger from '../utils/logger';
import {
  detectGaps,
  fillTimeGaps,
  formatGapDuration,
  type GapPeriod,
} from '../utils/telemetryGapFill';

interface SensorData {
  timestamp: Date;
  temperature?: number;
  humidity?: number;
  level?: number; // Tank level in cm
  battery?: number; // Battery voltage
  rssi?: number; // Signal strength
  pressure?: number; // Pressure (future)
  voltage?: number; // Voltage (future)
  [key: string]: any; // Allow dynamic metrics
}

interface OptimizedTelemetryChartProps {
  data: SensorData[];
  metrics: string[];
  theme: any;
  isArabic: boolean;
  timeRange: '24h' | '30d' | 'custom';
  isFullscreen?: boolean;
  onFullscreen?: () => void;
  title?: string; // Chart title for fullscreen display
}

// Metric display configuration
const METRIC_CONFIG: Record<
  string,
  {
    color: string;
    gradientStart: string;
    labelAr: string;
    labelEn: string;
    unit: string;
    icon: string;
  }
> = {
  temperature: {
    color: '#ef4444',
    gradientStart: '#fca5a5',
    labelAr: 'الحرارة',
    labelEn: 'Temperature',
    unit: '°C',
    icon: 'thermometer-outline',
  },
  humidity: {
    color: '#3b82f6',
    gradientStart: '#93c5fd',
    labelAr: 'الرطوبة',
    labelEn: 'Humidity',
    unit: '%',
    icon: 'water-outline',
  },
  level: {
    color: '#06b6d4',
    gradientStart: '#67e8f9',
    labelAr: 'المستوى',
    labelEn: 'Level',
    unit: 'cm',
    icon: 'speedometer-outline',
  },
  battery: {
    color: '#84cc16',
    gradientStart: '#bef264',
    labelAr: 'البطارية',
    labelEn: 'Battery',
    unit: 'V',
    icon: 'battery-charging-outline',
  },
  rssi: {
    color: '#6366f1',
    gradientStart: '#a5b4fc',
    labelAr: 'الإشارة',
    labelEn: 'Signal',
    unit: 'dBm',
    icon: 'wifi-outline',
  },
  pressure: {
    color: '#f59e0b',
    gradientStart: '#fcd34d',
    labelAr: 'الضغط',
    labelEn: 'Pressure',
    unit: 'hPa',
    icon: 'cloud-outline',
  },
  voltage: {
    color: '#8b5cf6',
    gradientStart: '#c4b5fd',
    labelAr: 'الجهد',
    labelEn: 'Voltage',
    unit: 'V',
    icon: 'flash-outline',
  },
};

const calculateLabelCount = (
  dataLength: number,
  screenWidth: number,
  isFullscreen: boolean,
): number => {
  const maxLabels = isFullscreen ? 10 : 5;
  const minSpacing = 60;
  const availableWidth = screenWidth - 80;
  const maxLabelsForWidth = Math.floor(availableWidth / minSpacing);
  return Math.min(maxLabels, maxLabelsForWidth, Math.max(3, dataLength));
};

const formatTimestamp = (date: Date, timeRange: string, isArabic: boolean): string => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return '';
  }

  const hour = date.getHours();
  const minute = date.getMinutes();
  const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  const day = date.getDate();
  const month = date.getMonth() + 1;

  // For 24h, show time only
  if (timeRange === '24h') {
    return timeStr;
  }

  // For 30d and custom, show date + time
  return isArabic ? `${day}/${month}\n${timeStr}` : `${month}/${day}\n${timeStr}`;
};

/**
 * Prepare chart data with smart label selection
 * ⚡ Now supports any metric dynamically
 */
const prepareChartData = (
  data: SensorData[],
  metric: string, // ⚡ Changed from literal type to string for flexibility
  screenWidth: number,
  timeRange: string,
  isArabic: boolean,
  isFullscreen: boolean,
) => {
  if (!data || data.length === 0) {
    return {
      labels: [],
      datasets: [{ data: [0] }],
    };
  }

  // Fill gaps with null markers so chart line breaks at offline periods
  const filledData = fillTimeGaps(data);

  // Extract values — gap slots become 0 (chart-kit doesn't support null in array)
  // We mark them via a separate color array
  const values = filledData.map((d) => {
    if (d.isGap) return null as unknown as number;
    const value = d[metric as keyof SensorData];
    return value !== undefined && value !== null && !isNaN(Number(value))
      ? Number(value)
      : (null as unknown as number);
  });

  const validValues = values.filter((v) => v !== null && v !== (null as unknown as number));

  logger.log(`📊 Chart ${metric} values:`, {
    totalPoints: filledData.length,
    validValues: validValues.length,
    gapPoints: filledData.filter((d) => d.isGap).length,
    hasValidData: validValues.length > 0,
  });

  if (validValues.length === 0) {
    logger.warn(`⚠️ No valid ${metric} data for chart`);
    return {
      labels: [''],
      datasets: [{ data: [0] }],
    };
  }

  // Calculate how many labels to show (based on filled data length)
  const labelCount = calculateLabelCount(filledData.length, screenWidth, isFullscreen);
  const step = Math.max(1, Math.floor(filledData.length / labelCount));

  // Generate labels — gap slots get empty string
  const labels = filledData.map((d, index) => {
    if (d.isGap) return '';
    if (index % step === 0 || index === filledData.length - 1) {
      return formatTimestamp(d.timestamp, timeRange, isArabic);
    }
    return '';
  });

  // Replace null with nearest valid neighbour (forward-fill) for chart rendering
  // but keep track of which indices are gaps for color coding
  const chartValues = [...values];
  // Forward-fill: propagate last valid value into gap slots
  let lastValid: number | null = null;
  for (let i = 0; i < chartValues.length; i++) {
    if (chartValues[i] !== null && chartValues[i] !== (null as unknown as number)) {
      lastValid = chartValues[i];
    } else if (lastValid !== null) {
      chartValues[i] = lastValid;
    }
  }
  // Back-fill first gap if no prior value
  if (lastValid !== null) {
    for (let i = 0; i < chartValues.length; i++) {
      if (chartValues[i] === null || chartValues[i] === (null as unknown as number)) {
        chartValues[i] = lastValid;
      } else {
        break;
      }
    }
  }

  const safeValues = chartValues.map((v) =>
    v === null || v === (null as unknown as number) ? 0 : v,
  );

  return {
    labels,
    datasets: [
      {
        data: safeValues.length > 0 ? safeValues : [0],
        color: (opacity = 1) =>
          metric === 'temperature'
            ? `rgba(239, 68, 68, ${opacity})`
            : metric === 'humidity'
            ? `rgba(59, 130, 246, ${opacity})`
            : `rgba(99, 102, 241, ${opacity})`,
        strokeWidth: 2,
      },
    ],
  };
};

const OptimizedTelemetryChart = React.memo<OptimizedTelemetryChartProps>(
  ({ data, metrics, theme, isArabic, timeRange, isFullscreen = false, onFullscreen }) => {
    const { width: windowWidth, height: windowHeight } = useWindowDimensions();
    const [tooltipPos, setTooltipPos] = useState<{
      x: number;
      y: number;
      value: number;
      index: number;
      metric: string;
    } | null>(null);
    const [activeMetric, setActiveMetric] = useState<string>(() => metrics?.[0] ?? '');

    const showHorizontalLabels = Platform.OS !== 'web';
    // Always use reactive windowWidth — fixes landscape not adapting
    const chartWidth = isFullscreen ? windowWidth - 32 : windowWidth - 32;
    const chartHeight = isFullscreen ? windowHeight - 160 : 220;

    // Keep activeMetric in sync if metrics prop changes
    const resolvedMetric = metrics?.includes(activeMetric) ? activeMetric : metrics?.[0] ?? '';

    const hexToRgba = (hex: string, opacity: number = 1) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    };

    // Detect gap periods once for display in the gap badges section
    const gapPeriods = useMemo((): GapPeriod[] => {
      if (!data || data.length < 2) return [];
      return detectGaps(data, isArabic);
    }, [data, isArabic]);

    const chartDataByMetric = useMemo(() => {
      const result: Record<string, { data: any; cfg: (typeof METRIC_CONFIG)[string] }> = {};
      (metrics ?? []).forEach((metric) => {
        result[metric] = {
          data: prepareChartData(data, metric, chartWidth, timeRange, isArabic, isFullscreen),
          cfg: METRIC_CONFIG[metric] ?? {
            color: '#64748b',
            gradientStart: '#94a3b8',
            labelAr: metric,
            labelEn: metric,
            unit: '',
            icon: 'analytics-outline',
          },
        };
      });
      return result;
    }, [data, metrics, chartWidth, timeRange, isArabic, isFullscreen]);

    const activeEntry = chartDataByMetric[resolvedMetric];
    const accentColor = activeEntry?.cfg.color ?? theme.colors.primary;

    // Compute min/max for stat chips
    const statsForMetric = useMemo(() => {
      if (!data || data.length === 0) return null;
      const values = data
        .map((d) => d[resolvedMetric])
        .filter((v): v is number => v !== undefined && v !== null && !isNaN(Number(v)))
        .map(Number);
      if (values.length === 0) return null;
      return {
        min: Math.min(...values),
        max: Math.max(...values),
        avg: values.reduce((a, b) => a + b, 0) / values.length,
        latest: values[values.length - 1],
      };
    }, [data, resolvedMetric]);

    const unit = activeEntry?.cfg.unit ?? '';

    if (!data || data.length === 0 || !metrics || metrics.length === 0) {
      return (
        <View style={[styles.container, { backgroundColor: theme.colors.card }]}>
          <View style={[styles.accentBar, { backgroundColor: theme.colors.primary }]} />
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <Ionicons name="analytics-outline" size={20} color={theme.colors.primary} />
              <Text style={[styles.title, { color: theme.colors.text }]}>
                {isArabic ? 'الرسم البياني' : 'Telemetry Chart'}
              </Text>
            </View>
          </View>
          <View style={[styles.emptyState, { height: 180 }]}>
            <Ionicons name="bar-chart-outline" size={48} color={theme.colors.border} />
            <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
              {isArabic ? 'لا توجد بيانات للعرض' : 'No data available'}
            </Text>
          </View>
        </View>
      );
    }

    const chartConfig = {
      backgroundColor: theme.colors.card,
      backgroundGradientFrom: theme.colors.card,
      backgroundGradientTo: theme.colors.card,
      decimalPlaces: 1,
      color: (opacity = 1) => hexToRgba(accentColor, opacity),
      labelColor: () => theme.colors.textSecondary,
      style: { borderRadius: 12 },
      propsForDots: { r: '0', strokeWidth: '0' },
      propsForBackgroundLines: {
        strokeDasharray: '4,4',
        stroke: theme.colors.border,
        strokeWidth: 0.8,
      },
      fillShadowGradient: accentColor,
      fillShadowGradientOpacity: 0.12,
    };

    const hasValidData = (activeEntry?.data?.datasets?.[0]?.data?.length ?? 0) > 0;

    return (
      <View style={[styles.container, { backgroundColor: theme.colors.card }]}>
        {/* Colored top accent bar */}
        <View style={[styles.accentBar, { backgroundColor: accentColor }]} />

        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <View style={[styles.iconCircle, { backgroundColor: hexToRgba(accentColor, 0.15) }]}>
              <Ionicons
                name={(activeEntry?.cfg.icon ?? 'analytics-outline') as any}
                size={18}
                color={accentColor}
              />
            </View>
            <Text style={[styles.title, { color: theme.colors.text }]}>
              {isArabic ? 'الرسم البياني' : 'Telemetry Chart'}
            </Text>
          </View>
          {!isFullscreen && onFullscreen && (
            <TouchableOpacity
              onPress={onFullscreen}
              style={[styles.fullscreenBtn, { backgroundColor: hexToRgba(accentColor, 0.1) }]}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons name="expand-outline" size={18} color={accentColor} />
            </TouchableOpacity>
          )}
        </View>

        {/* Metric Tabs — only shown when multiple metrics available */}
        {metrics.length > 1 && (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.tabsScroll}
          >
            {metrics.map((m) => {
              const cfg = METRIC_CONFIG[m] ?? {
                color: '#64748b',
                labelAr: m,
                labelEn: m,
                icon: 'analytics-outline',
              };
              const isActive = m === resolvedMetric;
              return (
                <TouchableOpacity
                  key={m}
                  onPress={() => setActiveMetric(m)}
                  style={[
                    styles.tab,
                    isActive
                      ? { backgroundColor: cfg.color, borderColor: cfg.color }
                      : { backgroundColor: 'transparent', borderColor: theme.colors.border },
                  ]}
                >
                  <Ionicons
                    name={cfg.icon as any}
                    size={14}
                    color={isActive ? '#fff' : theme.colors.textSecondary}
                  />
                  <Text
                    style={[
                      styles.tabText,
                      { color: isActive ? '#fff' : theme.colors.textSecondary },
                    ]}
                  >
                    {isArabic ? cfg.labelAr : cfg.labelEn}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        )}

        {/* Stat Chips */}
        {statsForMetric && (
          <View style={styles.statsRow}>
            {[
              { labelEn: 'Latest', labelAr: 'الأخيرة', val: statsForMetric.latest },
              { labelEn: 'Avg', labelAr: 'المتوسط', val: statsForMetric.avg },
              { labelEn: 'Min', labelAr: 'الأدنى', val: statsForMetric.min },
              { labelEn: 'Max', labelAr: 'الأعلى', val: statsForMetric.max },
            ].map((chip) => (
              <View
                key={chip.labelEn}
                style={[
                  styles.statChip,
                  {
                    borderColor: hexToRgba(accentColor, 0.3),
                    backgroundColor: hexToRgba(accentColor, 0.06),
                  },
                ]}
              >
                <Text style={[styles.statChipValue, { color: accentColor }]}>
                  {chip.val.toFixed(1)}
                  {unit}
                </Text>
                <Text style={[styles.statChipLabel, { color: theme.colors.textSecondary }]}>
                  {isArabic ? chip.labelAr : chip.labelEn}
                </Text>
              </View>
            ))}
          </View>
        )}

        {/* Chart */}
        {hasValidData ? (
          <View style={styles.chartWrapper}>
            <LineChart
              data={activeEntry!.data}
              width={chartWidth}
              height={chartHeight}
              chartConfig={chartConfig}
              bezier
              withDots={activeEntry!.data.datasets[0].data.length < 24}
              withInnerLines={true}
              withOuterLines={false}
              withVerticalLines={false}
              withHorizontalLines={true}
              withVerticalLabels={true}
              withHorizontalLabels={showHorizontalLabels}
              fromZero={false}
              segments={4}
              style={styles.chart}
              decorator={() => {
                if (!tooltipPos || tooltipPos.metric !== resolvedMetric) return null;
                return (
                  <View>
                    <View
                      style={[
                        styles.tooltip,
                        {
                          left: tooltipPos.x - 52,
                          top: tooltipPos.y - 68,
                          borderColor: accentColor,
                          backgroundColor: theme.colors.card,
                        },
                      ]}
                    >
                      <Text style={[styles.tooltipValue, { color: accentColor }]}>
                        {tooltipPos.value.toFixed(1)}
                        {unit}
                      </Text>
                      <Text style={[styles.tooltipTime, { color: theme.colors.textSecondary }]}>
                        {activeEntry!.data.labels[tooltipPos.index]}
                      </Text>
                    </View>
                    <View
                      style={[
                        styles.tooltipDot,
                        {
                          left: tooltipPos.x - 5,
                          top: tooltipPos.y - 5,
                          backgroundColor: accentColor,
                        },
                      ]}
                    />
                  </View>
                );
              }}
              onDataPointClick={(pointData) => {
                setTooltipPos({
                  x: pointData.x,
                  y: pointData.y,
                  value: pointData.value,
                  index: pointData.index,
                  metric: resolvedMetric,
                });
                setTimeout(() => setTooltipPos(null), 3000);
              }}
            />
          </View>
        ) : (
          <View style={[styles.emptyState, { height: chartHeight }]}>
            <Ionicons name="bar-chart-outline" size={40} color={theme.colors.border} />
            <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
              {isArabic ? 'لا توجد بيانات للمقياس المحدد' : 'No data for selected metric'}
            </Text>
          </View>
        )}

        {/* Gap Periods — shown only when offline periods exist */}
        {gapPeriods.length > 0 && (
          <View style={styles.gapSection}>
            <View style={styles.gapHeader}>
              <Ionicons name="warning-outline" size={13} color="#ef4444" />
              <Text style={styles.gapHeaderText}>
                {isArabic
                  ? `${gapPeriods.length} فترة انقطاع`
                  : `${gapPeriods.length} offline period${gapPeriods.length > 1 ? 's' : ''}`}
              </Text>
            </View>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.gapBadgesScroll}
            >
              {gapPeriods.map((gap, idx) => {
                const fromStr = gap.from.toLocaleTimeString(isArabic ? 'ar-SA' : 'en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                });
                const toStr = gap.to.toLocaleTimeString(isArabic ? 'ar-SA' : 'en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                });
                const dateStr = gap.from.toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', {
                  month: 'short',
                  day: 'numeric',
                });
                return (
                  <View key={idx} style={styles.gapBadge}>
                    <View style={styles.gapBadgeTop}>
                      <View style={styles.gapDot} />
                      <Text style={styles.gapDuration}>
                        {formatGapDuration(gap.durationHours, isArabic)}
                      </Text>
                      <Text style={styles.gapLabel}>{isArabic ? 'انقطاع' : 'offline'}</Text>
                    </View>
                    <Text style={styles.gapTime}>
                      {dateStr} {fromStr} – {toStr}
                    </Text>
                  </View>
                );
              })}
            </ScrollView>
          </View>
        )}

        {/* Footer */}
        <View style={[styles.footer, { borderTopColor: theme.colors.border }]}>
          <Text style={[styles.dataInfo, { color: theme.colors.textSecondary }]}>
            {isArabic ? `${data.length} نقطة` : `${data.length} pts`}
            {gapPeriods.length > 0 && (
              <Text style={{ color: '#ef4444' }}>
                {isArabic
                  ? ` · ${gapPeriods.length} انقطاع`
                  : ` · ${gapPeriods.length} gap${gapPeriods.length > 1 ? 's' : ''}`}
              </Text>
            )}
          </Text>
          <View style={[styles.liveChip, { backgroundColor: hexToRgba(accentColor, 0.12) }]}>
            <View style={[styles.liveDot, { backgroundColor: accentColor }]} />
            <Text style={[styles.liveText, { color: accentColor }]}>
              {isArabic ? 'مباشر' : 'Live'}
            </Text>
          </View>
        </View>
      </View>
    );
  },
);

OptimizedTelemetryChart.displayName = 'OptimizedTelemetryChart';

export default OptimizedTelemetryChart;

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 16,
    marginBottom: 16,
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 4,
  },
  accentBar: {
    height: 4,
    width: '100%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 10,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  iconCircle: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 15,
    fontWeight: '700',
    letterSpacing: 0.2,
  },
  fullscreenBtn: {
    width: 34,
    height: 34,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabsScroll: {
    paddingHorizontal: 16,
    paddingBottom: 10,
    gap: 8,
    flexDirection: 'row',
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    borderWidth: 1.5,
  },
  tabText: {
    fontSize: 12,
    fontWeight: '600',
  },
  statsRow: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingBottom: 12,
    gap: 8,
  },
  statChip: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 1,
  },
  statChipValue: {
    fontSize: 13,
    fontWeight: '700',
  },
  statChipLabel: {
    fontSize: 10,
    marginTop: 2,
  },
  chartWrapper: {
    alignItems: 'center',
    overflow: 'hidden',
  },
  chart: {
    borderRadius: 0,
  },
  tooltip: {
    position: 'absolute',
    borderRadius: 10,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderWidth: 1.5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 6,
    minWidth: 104,
    alignItems: 'center',
  },
  tooltipValue: {
    fontSize: 13,
    fontWeight: '700',
  },
  tooltipTime: {
    fontSize: 10,
    marginTop: 2,
  },
  tooltipDot: {
    position: 'absolute',
    width: 10,
    height: 10,
    borderRadius: 5,
    borderWidth: 2,
    borderColor: '#fff',
  },
  emptyState: {
    justifyContent: 'center',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 16,
  },
  emptyText: {
    fontSize: 13,
    textAlign: 'center',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderTopWidth: 1,
    marginTop: 4,
  },
  dataInfo: {
    fontSize: 11,
  },
  liveChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 20,
  },
  liveDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  liveText: {
    fontSize: 11,
    fontWeight: '600',
  },
  gapSection: {
    paddingHorizontal: 16,
    paddingBottom: 10,
    paddingTop: 4,
    borderTopWidth: 1,
    borderTopColor: '#fecaca',
    backgroundColor: '#fff5f5',
  },
  gapHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginBottom: 6,
  },
  gapHeaderText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#ef4444',
  },
  gapBadgesScroll: {
    gap: 8,
    flexDirection: 'row',
  },
  gapBadge: {
    backgroundColor: '#fee2e2',
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#fca5a5',
    minWidth: 120,
  },
  gapBadgeTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginBottom: 2,
  },
  gapDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#ef4444',
  },
  gapDuration: {
    fontSize: 13,
    fontWeight: '700',
    color: '#dc2626',
  },
  gapLabel: {
    fontSize: 11,
    color: '#ef4444',
  },
  gapTime: {
    fontSize: 10,
    color: '#b91c1c',
  },
});
